import { useState, useRef, useEffect } from 'react';
import { TopicSentence } from '../types';

interface Props {
  sentences: TopicSentence[];
  initialAnswers: Map<number, string>;
  onSubmit: (answers: Map<number, string>) => void;
  onBack: () => void;
}

export default function DictationPhase({ sentences, initialAnswers, onSubmit, onBack }: Props) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<Map<number, string>>(new Map(initialAnswers));
  const [showHint, setShowHint] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const current = sentences[currentIdx];
  const total = sentences.length;
  const isLastSentence = currentIdx === total - 1;

  const updateAnswer = (value: string) => {
    setAnswers((prev) => new Map(prev).set(current.index, value));
  };

  const goPrev = () => {
    if (currentIdx > 0) {
      setCurrentIdx(currentIdx - 1);
      setShowHint(false);
    }
  };

  const goNext = () => {
    if (currentIdx < total - 1) {
      setCurrentIdx(currentIdx + 1);
      setShowHint(false);
    }
  };

  // Enter 流：下一句或提交
  const handleEnterAction = () => {
    if (isLastSentence) {
      onSubmit(answers);
    } else {
      goNext();
    }
  };

  // 自动聚焦 textarea
  useEffect(() => {
    textareaRef.current?.focus();
  }, [currentIdx]);

  // 处理 textarea 内的快捷键
  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const key = e.key;

    // Enter = 下一句 / 提交（Shift+Enter 保留为换行）
    if (key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEnterAction();
      return;
    }

    // ← 方向键 = 上一句（只在光标在开头时触发，避免影响文本编辑）
    if (key === 'ArrowLeft') {
      const textarea = e.currentTarget;
      const cursorAtStart = textarea.selectionStart === 0 && textarea.selectionEnd === 0;
      if (cursorAtStart) {
        e.preventDefault();
        goPrev();
        return;
      }
    }

    // → 方向键 = 下一句（只在光标在末尾时触发）
    if (key === 'ArrowRight') {
      const textarea = e.currentTarget;
      const cursorAtEnd = textarea.selectionStart === textarea.value.length;
      if (cursorAtEnd) {
        e.preventDefault();
        if (isLastSentence) {
          onSubmit(answers);
        } else {
          goNext();
        }
        return;
      }
    }

    // Escape = 返回浏览
    if (key === 'Escape') {
      e.preventDefault();
      onBack();
      return;
    }
  };

  const filledCount = Array.from(answers.values()).filter((a) => a.trim()).length;

  if (!current) {
    return <div className="topic-recall-content">无可用句子</div>;
  }

  return (
    <div className="topic-recall-content topic-dictation-phase">
      {/* 进度指示 */}
      <div className="topic-dictation-progress">
        <span className="topic-dictation-progress-text">
          句子 {currentIdx + 1}/{total}
        </span>
        <div className="topic-dictation-progress-bar">
          <div
            className="topic-dictation-progress-fill"
            style={{ width: `${((currentIdx + 1) / total) * 100}%` }}
          />
        </div>
      </div>

      {/* 中文提示 - 点击展开 */}
      <div className="topic-dictation-prompt">
        <button
          type="button"
          className="topic-dictation-hint-toggle"
          onClick={() => setShowHint(!showHint)}
        >
          {showHint ? '▼ 隐藏提示' : '▶ 查看中文提示'}
        </button>
        {showHint && <p className="topic-dictation-prompt-text">{current.chinese}</p>}
      </div>

      {/* 输入区域 */}
      <div className="writing-input-area">
        <div className="writing-input-row">
          <textarea
            ref={textareaRef}
            className="writing-textarea"
            rows={3}
            value={answers.get(current.index) || ''}
            onChange={(e) => updateAnswer(e.target.value)}
            onKeyDown={handleTextareaKeyDown}
            placeholder="默写英文... (Enter 下一句)"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
        </div>
      </div>

      {/* 导航按钮 */}
      <div className="topic-dictation-nav">
        <button
          type="button"
          className="session-action neutral"
          onClick={goPrev}
          disabled={currentIdx === 0}
        >
          ← 上一句
        </button>
        <button
          type="button"
          className="session-action neutral"
          onClick={isLastSentence ? () => onSubmit(answers) : goNext}
        >
          {isLastSentence ? '提交全部 ↵' : '下一句 →'}
        </button>
      </div>

      {/* 底部操作 */}
      <div className="topic-recall-ratio">
        <button type="button" className="session-action neutral" onClick={onBack}>
          返回浏览 (Esc)
        </button>
        <span className="topic-dictation-filled-count">
          已填 {filledCount}/{total}
        </span>
      </div>
    </div>
  );
}
